import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, AlertTriangle, XCircle, FileText } from 'lucide-react';
import type { SimulationResponse } from '@/types/simulation.ts';
import { useAuth } from '@/context/AuthContext.tsx';

interface DocumentsStatusBadgeProps {
  simulation: SimulationResponse;
  /**
   * Override the role resolved from useAuth. Optional — the badge only needs
   * this when rendered in a context where the current user's role does not
   * match the view (rare).
   */
  role?: 'manager' | 'salesperson';
  /** Render a placeholder badge when the simulation has no documents instead of null. */
  showWhenEmpty?: boolean;
}

export default function DocumentsStatusBadge({ simulation, role, showWhenEmpty }: DocumentsStatusBadgeProps) {
  const { t } = useTranslation();
  const { isSalesManager } = useAuth();
  const effectiveRole: 'manager' | 'salesperson' = role ?? (isSalesManager ? 'manager' : 'salesperson');

  const {
    documentsReadiness,
    processingDocumentsCount,
    readyDocumentsCount,
    failedDocumentsCount,
    rejectedDocumentsCount,
  } = simulation;

  if (documentsReadiness === 'NoDocuments') {
    if (!showWhenEmpty) return null;
    return (
      <Badge tone="muted-soft" icon={<FileText className="size-3.5" />}>
        {t(`documents.readiness.${effectiveRole}.none`)}
      </Badge>
    );
  }

  if (effectiveRole === 'manager') {
    switch (documentsReadiness) {
      case 'Ready':
        return (
          <Badge tone="success" icon={<CheckCircle2 className="size-3.5" />}>
            {t('documents.readiness.manager.ready')}
          </Badge>
        );
      case 'Processing':
        return (
          <Badge tone="info" icon={<Loader2 className="size-3.5 animate-spin" />}>
            {t('documents.readiness.manager.processing', { count: processingDocumentsCount })}
          </Badge>
        );
      case 'PartiallyFailed':
        return (
          <Badge tone="warning" icon={<AlertTriangle className="size-3.5" />}>
            {t('documents.readiness.manager.partiallyFailed', {
              failed: failedDocumentsCount,
              rejected: rejectedDocumentsCount,
              ready: readyDocumentsCount,
            })}
          </Badge>
        );
      case 'Failed':
        return (
          <Badge tone="danger" icon={<XCircle className="size-3.5" />}>
            {t('documents.readiness.manager.failed', {
              failed: failedDocumentsCount,
              rejected: rejectedDocumentsCount,
            })}
          </Badge>
        );
    }
  }

  // salesperson
  switch (documentsReadiness) {
    case 'Ready':
    case 'PartiallyFailed':
      return (
        <Badge tone="success" icon={<CheckCircle2 className="size-3.5" />}>
          {t('documents.readiness.salesperson.ready')}
        </Badge>
      );
    case 'Processing':
      return (
        <Badge tone="muted" icon={<Loader2 className="size-3.5 animate-spin" />}>
          {t('documents.readiness.salesperson.processing')}
        </Badge>
      );
    case 'Failed':
      return (
        <Badge tone="muted-soft">
          {t('documents.readiness.salesperson.noMaterial')}
        </Badge>
      );
  }

  return null;
}

type Tone = 'success' | 'info' | 'warning' | 'danger' | 'muted' | 'muted-soft';

const toneClass: Record<Tone, string> = {
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  info: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  danger: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  muted: 'bg-muted text-muted-foreground',
  'muted-soft': 'bg-muted/50 text-muted-foreground/80',
};

function Badge({ tone, icon, children }: { tone: Tone; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${toneClass[tone]}`}
    >
      {icon}
      <span>{children}</span>
    </span>
  );
}
